import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { EURO_SYMBOL, billStatus as NAVStatus } from 'utils/constants';
import { parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { ActionButton, ConfirmDialog, CustomTextField } from 'components';
import { TableRow, TableCell } from '@mui/material';
import { useActivateNAVMutation } from 'api/main';

const userFriendlyStatuses = {
    [NAVStatus.CREATED]: texts.CREATED,
    [NAVStatus.ACTIVATED]: texts.ACTIVATED,
    [NAVStatus.COMPLETED]: texts.COMPLETED,
};

const NAVSubcategoryRow = ({ subcategory }) => {
    const [scVL, setScVL] = useState(subcategory?.nav?.vl >= 0 ? subcategory?.nav?.vl : '');
    const [formError, setFormError] = useState('');
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);

    const [activateNAV, { isLoading: activateLoading, isSuccess: activateSuccess }] =
        useActivateNAVMutation();

    const handleChangeVL = e => {
        const value = e.target.value;
        setScVL(value !== '' ? +value : value);
        setFormError('');
    };

    const handleValidate = useCallback(() => {
        if (scVL === '' || isNaN(scVL) || scVL < 0) {
            setFormError(texts.SAISIR_LE_CHAMP);
            return;
        }
        setFormError('');
        setActivateDialogOpen(true);
    }, [scVL]);

    const handleActivate = useCallback(() => {
        activateNAV({ id: subcategory?.nav?.id, value: +scVL });
    }, [activateNAV, subcategory?.nav?.id, scVL]);

    useEffect(() => {
        if (activateSuccess) {
            setActivateDialogOpen(false);
        }
    }, [activateSuccess]);

    return (
        <TableRow key={subcategory.id}>
            <TableCell>{subcategory.name}</TableCell>
            <TableCell>{moment(subcategory.nav?.date).format('L')}</TableCell>
            <TableCell>
                {subcategory.nav?.status?.toLowerCase() === NAVStatus.CREATED ? (
                    <CustomTextField
                        formatNumber
                        placeholder={`VL (${EURO_SYMBOL})`}
                        error={formError}
                        formControlProps={{ sx: { mb: 0, maxWidth: '200px' } }}
                        showError={false}
                        value={scVL}
                        onChange={handleChangeVL}
                    />
                ) : (
                    subcategory.nav?.vl
                )}
            </TableCell>
            <TableCell>{userFriendlyStatuses[subcategory.nav?.status?.toLowerCase()]}</TableCell>
            <TableCell>
                {subcategory.nav?.status?.toLowerCase() === NAVStatus.CREATED
                    ? '-'
                    : moment(subcategory.nav?.activatedAt).format('L')}
            </TableCell>
            <TableCell>
                {subcategory.nav?.status?.toLowerCase() === NAVStatus.CREATED
                    ? '-'
                    : parseFullName(subcategory.nav?.activatedBy)}
            </TableCell>
            <TableCell>
                <ActionButton
                    variant="contained"
                    size="small"
                    onClick={handleValidate}
                    disabled={subcategory.nav?.status?.toLowerCase() !== NAVStatus.CREATED}
                >
                    {texts.PUBLISH}
                </ActionButton>
                <ConfirmDialog
                    open={activateDialogOpen}
                    title={texts.VL_PUBLISH_CONFIRMATION}
                    cancelText={texts.CANCEL}
                    confirmText={texts.CONFIRMER}
                    loading={activateLoading}
                    handleClose={() => setActivateDialogOpen(false)}
                    handleConfirm={handleActivate}
                >
                    {texts.CONFIRM_PUBLISHING_VL}
                </ConfirmDialog>
            </TableCell>
        </TableRow>
    );
};

NAVSubcategoryRow.propTypes = {
    subcategory: PropTypes.object.isRequired,
    nav: PropTypes.object,
};

export default NAVSubcategoryRow;
